import React from 'react';
import { AdventureHeader } from '../../types';
import { Button } from '../ui';
import { AdventureTitleImage } from '../ui/AdventureTitleImage';
import { renderRichText, isRichTextDescription, plainTextToRichText } from '../../utils/richTextRenderer';

export interface AdventureCardProps {
  adventure: AdventureHeader;
  adventureId: string;
  onSelect: (adventureId: string) => void;
  onCreateSession: (adventureId: string) => void;
  isSelected?: boolean;
  isCreating?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const AdventureCard: React.FC<AdventureCardProps> = ({
  adventure,
  adventureId,
  onSelect,
  onCreateSession,
  isSelected = false,
  isCreating = false,
  className = '',
  style = {}
}) => {
  const handleClick = () => {
    if (!isSelected) {
      onSelect(adventureId);
    }
  };

  const handleCreateSession = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCreateSession(adventureId);
  };

  return (
    <div
      data-testid="adventure-card"
      style={{
        backgroundColor: isSelected 
          ? 'rgba(59, 130, 246, 0.2)' 
          : 'rgba(0, 0, 0, 0.3)',
        border: isSelected 
          ? '2px solid rgba(59, 130, 246, 0.5)' 
          : '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: '0.75rem',
        padding: '1.25rem',
        cursor: isSelected ? 'default' : 'pointer',
        transition: 'all 0.3s ease-in-out',
        backdropFilter: 'blur(12px)',
        boxShadow: isSelected 
          ? '0 8px 25px -5px rgba(59, 130, 246, 0.3), 0 4px 6px -1px rgba(0, 0, 0, 0.2)' 
          : '0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.1)',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        ...style
      }}
      onClick={handleClick}
      className={`${isSelected ? '' : 'hover:shadow-lg hover:border-white/30 hover:bg-black/40 hover:-translate-y-1'} ${className}`}
    >
      <div className="flex flex-col h-full">
        {/* Adventure Title Image */}
        <div className="-mx-5 -mt-5 mb-3">
          <AdventureTitleImage 
            titleImageId={adventure.titleImageId}
            adventureId={adventureId}
            variant="card"
            alt={adventure.title}
            responsive={true}
          />
        </div>
        <h3 
          data-testid="adventure-title"
          className="text-lg font-semibold mb-2"
          style={{ 
            color: isSelected ? '#ffffff' : '#ffffff' 
          }}
        >
          {adventure.title}
        </h3>
        
        <div className="flex items-center justify-between mb-3">
          <div 
            className="text-xs font-medium"
            style={{ 
              color: isSelected ? '#c7d2fe' : '#9ca3af' 
            }}
          >
            {adventure.characters?.length || 0} character{adventure.characters?.length !== 1 ? 's' : ''}
          </div>
        </div>
        
        <div 
          className="text-sm mb-3 flex-grow rich-text"
          style={{ 
            color: isSelected ? '#e0e7ff' : '#e5e7eb' 
          }}
          dangerouslySetInnerHTML={{
            __html: isRichTextDescription(adventure.description) 
              ? renderRichText(adventure.description)
              : renderRichText(plainTextToRichText(adventure.description as string))
          }}
        />

        {isSelected && (
          <Button
            data-testid="create-adventure-button"
            onClick={handleCreateSession}
            disabled={isCreating}
            variant="glass-primary"
            size="sm"
            fullWidth
          >
            {isCreating ? 'Creating...' : 'Create Adventure'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default AdventureCard;
